import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";
import { EditableText } from "@/components/report/parts/EditableText";
import { ReportV2 } from "@/components/report/ReportV2";
import { getReportV2 } from "@/lib/serverComm";
import EventBus from "@/lib/eventBus";
import type { SlideStatus } from "@/common/decks";
import type { ReportConfigV2 } from "@/common/report-configuration";

interface SlideProps {
  id: string;
  title: string;
  speakerNotes: string;
  reportId?: string;
  errorMessage?: string;
  status: SlideStatus;
  onTitleChange: (slideId: string, title: string) => void;
  onSpeakerNotesChange: (slideId: string, content: string) => void;
  onMoveUp: (slideId: string) => void;
  onMoveDown: (slideId: string) => void;
  onEdit: (slideId: string) => void;
  onDelete: (slideId: string) => void;
}

const Slide: React.FC<SlideProps> = ({
  id,
  title,
  speakerNotes,
  reportId,
  status,
  onTitleChange,
  onSpeakerNotesChange,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
}) => {
  const [reportConfig, setReportConfig] = useState<ReportConfigV2 | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setReportLoading(true);
        setReportError(false);
        const report = await getReportV2(reportId as string);
        setReportConfig(report.config);
      } catch (error) {
        console.error("Error fetching report:", error);
        setReportError(true);
      } finally {
        setReportLoading(false);
      }
    };

    if (reportId && status === "complete") {
      fetchReport();
    }
  }, [reportId, status]);

  // EventBus listener for report refresh events
  useEffect(() => {
    const handleReportRefresh = (refreshedReportId: string) => {
      // Only refresh if this slide's report matches the refreshed report
      if (refreshedReportId === reportId && status === "complete") {
        console.log(`Refreshing slide report for reportId: ${reportId}`);
        const fetchReport = async () => {
          try {
            setReportLoading(true);
            setReportError(false);
            const report = await getReportV2(reportId);
            setReportConfig(report.config);
            console.log(
              `Slide report refreshed successfully for reportId: ${reportId}`,
            );
          } catch (error) {
            console.error("Error refreshing slide report:", error);
            setReportError(true);
          } finally {
            setReportLoading(false);
          }
        };
        fetchReport();
      }
    };

    EventBus.$on("report:refresh", handleReportRefresh);

    return () => {
      EventBus.$off("report:refresh", handleReportRefresh);
    };
  }, [reportId, status]);
  const handleTitleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const content = e.target.value;
    onTitleChange(id, content);
  };

  const handleSpeakerChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const content = e.target.value;
    onSpeakerNotesChange(id, content);
  };

  const getStatusMessage = () => {
    switch (status) {
      case "pending":
        return "This slide is being prepared...";
      case "processing":
        return "This slide is being generated...";
      case "error":
        return "There was an error generating this slide";
      default:
        return "This slide is still being generated";
    }
  };

  // Render simplified version for non-complete statuses
  if (["processing", "pending"].includes(status)) {
    return (
      <div
        className="transition p-8 rounded-xl shadow-slide-card bg-white border-2 border-white hover:border-deck-primary"
        data-slide-id={id}
      >
        <div className="flex items-center justify-between gap-x-4 mb-5">
          <div className="flex-1 mr-4">
            <div className="text-2xl text-deck-primary font-bold">{title}</div>
          </div>
          <div className="flex items-center gap-x-4">
            <ArrowUp
              className="transition text-deck-primary cursor-pointer hover:text-black"
              size={20}
              onClick={() => onMoveUp(id)}
            />
            <ArrowDown
              className="transition text-deck-primary cursor-pointer hover:text-black"
              size={20}
              onClick={() => onMoveDown(id)}
            />
          </div>
        </div>
        <div
          className="flex items-center justify-center rounded-md bg-deck-500 text-deck-primary"
          style={{ height: "500px" }}
        >
          <span className="text-lg">{getStatusMessage()}</span>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="transition p-8 rounded-xl shadow-slide-card bg-white border-2 border-white hover:border-deck-primary">
        <div className="flex items-center justify-between gap-x-4 mb-5">
          <div className="flex-1 mr-4">
            <div className="text-2xl text-deck-primary font-bold">{title}</div>
          </div>
          <div className="flex items-center gap-x-4">
            <ArrowUp
              className="transition text-deck-primary cursor-pointer hover:text-black"
              size={20}
              onClick={() => onMoveUp(id)}
            />
            <ArrowDown
              className="transition text-deck-primary cursor-pointer hover:text-black"
              size={20}
              onClick={() => onMoveDown(id)}
            />
          </div>
        </div>
        <div
          className="flex flex-col items-center justify-center rounded-md bg-gray-50 text-gray-600 p-8 text-center"
          style={{ height: "500px" }}
        >
          <h3 className="text-xl font-semibold mb-4 text-gray-800">
            Something went wrong generating this slide.
          </h3>
          <p className="text-sm mb-6 max-w-md leading-relaxed">
            This might be temporary—try generating the report again. If the
            issue continues, please contact support@parita.com for help.
          </p>
          <button
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={() => onDelete(id)}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="transition p-8 rounded-xl shadow-slide-card bg-white border-2 border-white hover:border-deck-primary"
      data-slide-id={id}
    >
      <div className="flex items-center justify-between gap-x-4 mb-5">
        <div className="flex-1 mr-4">
          <EditableText
            text={title}
            onChange={handleTitleChange}
            canEdit={true}
            className="text-2xl text-deck-primary font-bold"
            component="input"
          />
        </div>
        <div className="flex items-center gap-x-4">
          <ArrowUp
            className="transition text-deck-primary cursor-pointer hover:text-black"
            size={20}
            onClick={() => onMoveUp(id)}
          />
          <ArrowDown
            className="transition text-deck-primary cursor-pointer hover:text-black"
            size={20}
            onClick={() => onMoveDown(id)}
          />
          <Pencil
            className="transition text-deck-primary cursor-pointer hover:text-black"
            size={20}
            onClick={() => onEdit(id)}
          />
          <Trash2
            className="transition text-deck-danger cursor-pointer hover:text-black"
            size={20}
            onClick={() => onDelete(id)}
          />
        </div>
      </div>
      <div className="block min-h-96 rounded-md mb-5 overflow-hidden">
        {reportLoading ? (
          <div className="h-96 bg-deck-500 rounded-md flex items-center justify-center">
            <span className="text-deck-primary">Loading report...</span>
          </div>
        ) : reportError ? (
          <div className="h-96 bg-red-50 rounded-md flex items-center justify-center border border-red-200">
            <span className="text-red-600">Error loading report</span>
          </div>
        ) : reportConfig ? (
          <div className="w-full">
            <ReportV2
              id={reportId}
              reportConfig={reportConfig}
              newFilterField={null}
              size="md"
              disableSummaryRegeneration={true}
              slideMode={true}
            />
          </div>
        ) : (
          <div className="h-96 bg-deck-500 rounded-md flex items-center justify-center">
            <span className="text-deck-primary">No report configured</span>
          </div>
        )}
      </div>
      <div className="block">
        <span className="block font-semibold text-deck-primary mb-2">
          Speaker notes
        </span>
        <div className="p-4 border border-deck-600 rounded-lg min-h-24">
          <EditableText
            text={speakerNotes}
            onChange={handleSpeakerChange}
            canEdit={true}
            className="text-deck-primary text-base min-h-24"
            component="textarea"
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Slide);
